import React from 'react';
import UFOLoadHold from '@atlaskit/react-ufo/load-hold';
import type { BillingSourceSystem } from '@atlassian/jira-billing/src/types.tsx';
import {
	type SERVICE_DESK_PROJECT,
	SOFTWARE_PROJECT,
	CORE_PROJECT,
} from '@atlassian/jira-common-constants/src/project-types.tsx';
import { expValEquals } from '@atlassian/jira-feature-experiments';
import { ContextualAnalyticsData, FireUiAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { useIsSiteAdmin } from '@atlassian/jira-tenant-context-controller/src/components/is-site-admin/index.tsx';
import { EditionAwarenessContainer } from '../../common/ui/styled/styled.tsx';
import { EditionAwarenessCtaButton } from './edition-awareness-cta-button/index.tsx';
import { UsersRemainingCtaButton } from './users-remaining-free-pill/index.tsx';
import { useCheckEditionAwarenessRequisites } from './utils.tsx';

export type FrePillSupportedProjects =
	| typeof SOFTWARE_PROJECT
	| typeof SERVICE_DESK_PROJECT
	// This should be a special case for the site when JWM is implicitly licensed with JSW
	| typeof CORE_PROJECT;

export type FreePillProps = {
	projectType: FrePillSupportedProjects;
	siteHasJswOnly: boolean;
};

export function FreePill(props: FreePillProps) {
	const { projectType, siteHasJswOnly } = props;

	if (projectType === CORE_PROJECT && !siteHasJswOnly) {
		throw new Error('CORE_PROJECT is only supported for Jira Software only sites');
	}
	const projectTypeToUpgrade = projectType === CORE_PROJECT ? SOFTWARE_PROJECT : projectType;

	const { userCanView, billingSourceSystem, isBillingAdmin } = useCheckEditionAwarenessRequisites({
		projectType: projectTypeToUpgrade,
	});

	if (userCanView === undefined || isBillingAdmin === undefined) {
		return <UFOLoadHold name="edition-awareness-requisites-loading" />;
	}

	if (userCanView) {
		if (projectType === CORE_PROJECT) {
			// For CONTROL and NOT-ENROLLED we are not showing the pill, but using the funtion to fire the exposure
			if (!expValEquals('edition_awareness_on_jwm', 'isEnabled', true)) {
				return null;
			}
		}
		return (
			<EditionAwarenessFreePill
				projectType={projectTypeToUpgrade}
				billingSourceSystem={billingSourceSystem}
				isBillingAdmin={isBillingAdmin}
				siteHasJswOnly={siteHasJswOnly}
			/>
		);
	}

	return null;
}

type EditionAwarenessFreePillProps = {
	projectType: typeof SOFTWARE_PROJECT | typeof SERVICE_DESK_PROJECT;
	billingSourceSystem: BillingSourceSystem;
	isBillingAdmin: boolean;
	siteHasJswOnly: boolean;
};

function EditionAwarenessFreePill(props: EditionAwarenessFreePillProps) {
	const { projectType, billingSourceSystem, isBillingAdmin, siteHasJswOnly } = props;
	const isSiteAdmin = useIsSiteAdmin();

	return (
		<ContextualAnalyticsData attributes={{ billingSourceSystem, isSiteAdmin, isBillingAdmin }}>
			<EditionAwarenessContainer>
				<ButtonForProduct projectType={projectType} siteHasJswOnly={siteHasJswOnly} />
				<FireUiAnalytics
					action="viewed"
					actionSubject="button"
					actionSubjectId="editionAwarenessFreePill"
				/>
			</EditionAwarenessContainer>
		</ContextualAnalyticsData>
	);
}

export type ButtonForProductProps = {
	projectType: FrePillSupportedProjects;
	siteHasJswOnly: boolean;
};
function ButtonForProduct(props: ButtonForProductProps) {
	const { projectType, siteHasJswOnly } = props;

	return projectType === SOFTWARE_PROJECT ? (
		<UsersRemainingCtaButton projectType={projectType} siteHasJswOnly={siteHasJswOnly} />
	) : (
		<EditionAwarenessCtaButton />
	);
}
